import { useMemo } from "react"

import { Button } from "@/components/ui"
import { cn } from "@/lib/utils"
import { IconAlertTriangle } from "@tabler/icons-react"

import { useAuth } from "../providers/Auth"

export interface ErrorStateProps {
  title: string
  subTitle?: string
  rawError?: any
  className?: string
  retryLabel?: string
  onRetry?: () => void
}

export function ErrorState({
  title,
  subTitle = "Please contact support@lumen.energy",
  rawError,
  retryLabel = "Try again",
  onRetry,
  className,
}: ErrorStateProps) {
  const { isAdmin } = useAuth()

  const graphqlError = useMemo(() => {
    if (!rawError) return undefined
    const query = rawError.query
    if (typeof query === "string" && rawError.variables) {
      return {
        query: query.trim(),
        variables: rawError.variables,
        message: rawError.message,
        extensions: rawError.extensions,
      }
    }
    return undefined
  }, [rawError])

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-6 rounded-xl border border-solid border-grey-200 bg-grey-100 p-5",
        className,
      )}
    >
      <IconAlertTriangle size={64} className="text-error-500" />
      <div className="flex flex-col items-center gap-2">
        <div className="text-body1 font-medium text-grey-800">{title}</div>
        <div className="text-center text-body2 whitespace-pre-line text-grey-700">
          {subTitle}
        </div>
      </div>
      {onRetry ? (
        <Button variant="neutral" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
      {isAdmin && (
        <div className="flex w-full flex-col gap-2 rounded-md bg-accent-3-400 p-4">
          <div className="text-center text-h4 font-medium whitespace-pre-line text-grey-900">
            Raw error (Admin only):
          </div>
          {graphqlError ? (
            <pre className="flex w-full flex-col gap-4 overflow-auto rounded-md bg-grey-200 p-5 font-mono text-body2 text-grey-700">
              <ErrorItem
                title="GraphQL error message"
                text={String(graphqlError.message)}
              />
              <ErrorItem title="Query" text={graphqlError.query} />
              <ErrorItem
                title="Variables"
                text={JSON.stringify(graphqlError.variables, null, 2)}
              />
              <ErrorItem
                title="Extensions"
                text={JSON.stringify(graphqlError.extensions, null, 2)}
              />
            </pre>
          ) : rawError ? (
            <pre className="w-full overflow-auto rounded-md bg-grey-200 p-5 font-mono text-body2 text-grey-700">
              {JSON.stringify(rawError, undefined, 2)}
            </pre>
          ) : null}
        </div>
      )}
    </div>
  )
}

const ErrorItem = ({ title, text }: { title: string; text: string }) => {
  if (!text) return null

  return (
    <div className="flex flex-col">
      <span className="text-colors-text-error">{title}: </span>
      <span className="max-h-[500px] overflow-auto whitespace-pre-wrap">{text}</span>
    </div>
  )
}
